import React, { memo } from 'react'
import styles from './CreateBannerSideDialog.module.scss'
import CommonSideDialog from '../../../../common/components/side-dialog/CommonSideDialog';
import { useForm } from 'react-hook-form';
import CommonFormFieldWrapper, { getFormInputErrors } from '../../../../common/components/form-field-wrapper/CommonFormFieldWrapper';
import CommonFileUploadInput, { StyleOfFileInput } from '../../../../common/components/file-upload-input/CommonFileUploadInput';
import { useCreateAdvertBannerMutation } from '../../../../service/advert-banner-service';
import { toast } from 'react-toastify';
import Button from '../../../../common/components/xepp/button/Button';

type Props = {
    onCloseDialog: () => void;
    onSuccess: () => void;
    setLoading: (status: boolean) => void;
}

type CreateAdvertBannerForm = {
    name: string;
    picture?: File;
    redirectUrl: string;
}

const CreateBannerSideDialog: React.FC<Props> = memo((props: Props) => {
    const { onCloseDialog, onSuccess, setLoading } = props;

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors, isValid, isSubmitted } 
    } = useForm<CreateAdvertBannerForm>({
        reValidateMode: 'onChange',
        mode: 'onBlur',
        defaultValues: {
            name: '',
            redirectUrl: ''
        }
    });

    const onFilesSelection = (files: File[]) => {
        if (files.length > 0) {
            setValue('picture', files[0], {
                shouldValidate: true,
                shouldTouch: true,
                shouldDirty: true,
            });
        }
    }

    const [createAdvertBanner, { isLoading, error }] = useCreateAdvertBannerMutation();

    const onSubmit = async (data: CreateAdvertBannerForm) => {
        const formData = new FormData();
        formData.append('name', data.name.trim());
        if (data.picture) {
            formData.append('picture', data.picture);
        }
        formData.append('redirectUrl', data.redirectUrl.trim());
    
        setLoading(true);
        try {
            await createAdvertBanner(formData).unwrap();
            onSuccess();
            toast.success('Advert Banner successfully created');
        } catch (e: any) {
            if (e?.data?.title) {
                toast.error(e.data.title);
            } else {
                toast.error('Failed to create advert banner');
            }
        } finally {
            onCloseDialog();
            setLoading(false);
        }
    };

    return (
        <CommonSideDialog onCloseDialog={onCloseDialog}>
            <form onSubmit={handleSubmit(onSubmit)} className={styles.container}>
                <div className={styles.header}>
                    <div>
                    <Button
                     onClick={props.onCloseDialog}
                      variant='outline'
                      text=""
                      rightIcon={<svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    d="M3.52214 7.33342H12.8346C13.0707 7.33342 13.2687 7.25356 13.4284 7.09384C13.5881 6.93412 13.668 6.7362 13.668 6.50009C13.668 6.26398 13.5881 6.06606 13.4284 5.90634C13.2687 5.74662 13.0707 5.66676 12.8346 5.66676H3.52214L7.60547 1.58342C7.77214 1.41676 7.852 1.22231 7.84505 1.00009C7.83811 0.777869 7.7513 0.583424 7.58464 0.416758C7.41797 0.26398 7.22352 0.184119 7.0013 0.177174C6.77908 0.17023 6.58464 0.250091 6.41797 0.416758L0.917969 5.91676C0.834636 6.00009 0.775608 6.09037 0.740886 6.18759C0.706163 6.28481 0.688803 6.38898 0.688803 6.50009C0.688803 6.6112 0.706163 6.71537 0.740886 6.81259C0.775608 6.90981 0.834636 7.00009 0.917969 7.08342L6.41797 12.5834C6.57075 12.7362 6.76172 12.8126 6.99089 12.8126C7.22005 12.8126 7.41797 12.7362 7.58464 12.5834C7.7513 12.4168 7.83464 12.2188 7.83464 11.9897C7.83464 11.7605 7.7513 11.5626 7.58464 11.3959L3.52214 7.33342Z"
                                                    fill="#1C1B1F"
                                                />
                                            </svg>}
                     />
                    {"  Create Banner"}
                    </div>
                    <div className={styles.controls}>
                        <Button
                          text="Create Banner"
                          disabled={isSubmitted && !isValid}
                          type="submit" />
                    </div>
                </div>
                <div className={styles.content}>
                    <CommonFormFieldWrapper title="Banner Name" required errors={getFormInputErrors(errors.name?.message)}>
                        <input {...register("name", { required: "Advert Banner Name is required" })} placeholder="Enter banner name" type="text" />
                    </CommonFormFieldWrapper>
                    <CommonFormFieldWrapper title="Banner image" required errors={getFormInputErrors(errors.picture?.message)}>
                        <CommonFileUploadInput
                            maxFileSize={24}
                            accept='image/*'
                            styleOfInput={StyleOfFileInput.FormInput}
                            registerInputProps={register("picture", { required: "Banner image is required" })}
                            onFilesSelect={onFilesSelection}
                        />
                    </CommonFormFieldWrapper>
                    <CommonFormFieldWrapper title="Redirect Url" required errors={getFormInputErrors(errors.redirectUrl?.message)}>
                        <input {...register("redirectUrl", { 
                                    required: "Redirect Url is required",
                                    pattern: {
                                        value: /^(https?:\/\/).+$/,
                                        message: "Redirect Url must be a valid URL starting with http:// or https://"
                                    }
                                })} 
                                placeholder="Enter URL" 
                                type="text" 
                        />
                    </CommonFormFieldWrapper>
                </div>
            </form>
        </CommonSideDialog>
    )
})

export default CreateBannerSideDialog